import { KEYCODE } from '@/constants';

/**
 * Class definition for `wp-expand` element
 *
 * @class WPExpand
 * @extends {HTMLElement}
 */
class WPExpand extends HTMLElement {
  constructor(self) {
    self = super(self);
    return self;
  }

  /**
   * Tag name of element
   *
   * @readonly
   * @static
   */
  static get is() {
    return 'wp-expand';
  }

  /**
   * Watched attributes
   *
   * @readonly
   * @static
   */
  static get observedAttributes() {
    return ['expanded'];
  }

  /**
   * Getter for `expanded` attribute
   */
  get expanded() {
    return this.hasAttribute('expanded');
  }

  /**
   * Setter for `expanded` attribute
   */
  set expanded(value) {
    if (Boolean(value)) {
      this.setAttribute('expanded', '');
    } else {
      this.removeAttribute('expanded');
    }
  }

  /**
   * Getter for `ready` attribute
   */
  get ready() {
    return this.hasAttribute('ready');
  }

  /**
   * Setter for `ready` attribute
   */
  set ready(value) {
    if (Boolean(value)) {
      this.setAttribute('ready', '');
    } else {
      this.removeAttribute('ready');
    }
  }

  /**
   * Getter for `animating` attribute
   */
  get animating() {
    return this.hasAttribute('animating');
  }

  /**
   * Setter for `animating` attribute
   */
  set animating(value) {
    if (Boolean(value)) {
      this.setAttribute('animating', '');
    } else {
      this.removeAttribute('animating');
    }
  }

  get _buttons() {
    return [...document.querySelectorAll(`a[href="#${this.id}"]`)];
  }

  connectedCallback() {
    if (!this.id) {
      console.warn('Element must have an ID', this);
      return;
    }

    if (!this.expanded) {
      this.style.height = 0;
    }

    this.ready = true;
    this.addEventListener('keydown', this._handleKeyDown, false);

    // Set up buttons
    if (this._buttons.length) {
      this._buttons.forEach((btn) => {
        if (this.expanded) {
          btn.setAttribute('aria-expanded', 'true');
        } else {
          btn.setAttribute('aria-expanded', 'false');
        }

        btn.addEventListener('click', this._handleBtnClick.bind(this), false);
      });
    }
  }

  _handleBtnClick(evt) {
    evt.preventDefault();
    evt.stopPropagation();

    // If animating, return
    if (this.animating) {
      return;
    }

    // Toggle expand
    this.expanded = !this.expanded;

    // Set aria attributes
    if (this.expanded) {
      evt.target.setAttribute('aria-expanded', 'true');
    } else {
      evt.target.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * Lifecycle event when watched attribute is changed
   *
   * @param {string} name Attribute name
   * @param {any} oldVal Old value of attribute
   * @param {any} newVal New value of attribute
   */
  attributeChangedCallback(name, oldVal, newVal) {
    switch (name) {
      case 'expanded':
        this._expandedChanged();
    }
  }

  /**
   * Called when `expanded` attribute is changed
   *
   * @private
   */
  _expandedChanged() {
    // If current height is 0 and aniating to 0, return
    if (this.style.height === '0px' && !this.expanded) {
      return;
    }

    // Set height
    this.style.height = `${this.scrollHeight}px`;
    this.animating = true;

    // If collapsed, set height to 0
    if (!this.expanded) {
      setTimeout(() => {
        this.style.height = 0;

        // Ensure animation has ended
        this._animate().then(() => {
          this.style.height = 0;
          this.animating = false;
        });
      }, 25);
    } else {
      // Scroll top
      this.children[0].scrollTop = 0;

      // After animation, reset height so panels can resize accordingly
      this._animate().then(() => {
        this.style.height = null;
        this.animating = false;
      });
    }
  }

  /**
   * Add event listener to ensure transition ends
   *
   * @private
   * @returns {Promise} Resolves when transition ends
   */
  _animate() {
    return new Promise((resolve) => {
      this.$once('transitionend', (evt) => {
        if (evt.target.tagName.toLowerCase() === WPExpand.is) {
          resolve();
        }
      });

      // Resolve promise if transitionend is never called using timeout
      setTimeout(() => {
        resolve();
      }, 500);
    });
  }

  _handleKeyDown(evt) {
    const keyCode = evt.which;

    // If expanded and ESC key, hide
    if (keyCode === KEYCODE.ESC && this.expanded) {
      this.expanded = false;
      return;
    }
  }
}

customElements.define(WPExpand.is, WPExpand);
